import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import Page from 'src/components/Page';
import useTable, { emptyRows } from 'src/hooks/useTable';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import {
  Box,
  Card,
  Container,
  debounce,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { PATH_DASHBOARD } from 'src/routes/paths';
import {
  LoadingTableRow,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from 'src/components/table';
import { translateTableHead } from 'src/careflow/helpers/common';
import { TABLE_HEAD_EPISODE_OF_CARE_LIST } from 'src/careflow/common/table-head';
import { useCallback, useEffect } from 'react';
import useObjectState from 'src/hooks/useObjectState';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { EpisodeOfCareService } from '../service/EpisodeOfCareService';
import useNotiMessage from 'src/hooks/useNotiMessage';
import EpisodeOfCareRow from '../components/Row';
import { TableToolbar } from 'src/careflow/common';

export default function EpisodeOfCareList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
    setPage,
  } = useTable();
  const { themeStretch } = useSettings();
  const { i18n } = useLocales();
  const { showErrorMessage } = useNotiMessage();

  const [{ data, search, isLoading }, updateState] = useObjectState<any>({
    data: [],
    search: null,
    isLoading: false,
  });

  const getPaginateQuery = (page: number, rowsPerPage: number, search: string) => {
    const paginateQuery: PaginateQuery = { page: page, limit: rowsPerPage };

    if (search) {
      paginateQuery.search = search;
    }

    return paginateQuery;
  };

  const fetchEpisodeOfCareList = async (page: number, rowsPerPage: number, search: string) => {
    updateState({ isLoading: true });

    const data = await EpisodeOfCareService.getAll(getPaginateQuery(page, rowsPerPage, search));
    if (!data) return showErrorMessage(i18n('errorMessages.error'));
    updateState({ data: data?.data, isLoading: false });
  };

  const getEpisodeOfCareList = useCallback(debounce(fetchEpisodeOfCareList, 600), []);

  useEffect(() => {
    getEpisodeOfCareList(page, rowsPerPage, search);
  }, [page, rowsPerPage, search]);

  const handleSearch = (query: string) => {
    updateState({ search: query });
    setPage(0);
  };

  const denseHeight = dense ? 52 : 72;
  const isNotFound = !data.length;

  return (
    <Page title={i18n('episodeOfCare.list.title', 'crs')}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          title={i18n('episodeOfCare.list.title', 'crs')}
          links={[
            { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
            { name: `${i18n('breadcrumbs.title', 'crs')}` },
            {
              name: i18n('episodeOfCare.list.title', 'crs'),
              href: PATH_DASHBOARD.crs.episodeOfCare.root,
            },
          ]}
          heading=""
          action={<></>}
        />
        <Card sx={{ width: '100%', height: '100%', pt: 3 }}>
          <Stack direction="row" sx={{ py: 2, px: 2 }}>
            <Grid item xs={6}>
              <TableToolbar filterMrn={search} onFilterMrn={handleSearch} />
            </Grid>
          </Stack>
          <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
            <Table size={dense ? 'small' : 'medium'}>
              <TableHeadCustom
                order={order}
                orderBy={orderBy}
                headLabel={translateTableHead(TABLE_HEAD_EPISODE_OF_CARE_LIST, i18n, 'crs')}
                rowCount={data.length}
                onSort={onSort}
              />
              {!isLoading ? (
                <TableBody>
                  {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <EpisodeOfCareRow key={row.id} row={row} />
                  ))}
                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(page, rowsPerPage, data.length)}
                  />

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              ) : (
                <LoadingTableRow colSpan={TABLE_HEAD_EPISODE_OF_CARE_LIST.length} />
              )}
            </Table>
          </TableContainer>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, position: { md: 'absolute' }, top: 0 }}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
